import { useEffect, useMemo, useRef, useState } from "react";

import {
  applyFleetOrderOperationalOverride,
  getFleetOrderOperationalOverrideWorkspace,
} from "../../api/client.js";
import { Dialog } from "../Dialog.jsx";
import { Icon } from "../Icon.jsx";

function phaseLabel(value) {
  const labels = {
    PREPARATION: "Persiapan di Depot",
    LOADING: "Loading",
    READY_TO_DEPART: "Siap Berangkat",
    IN_PROGRESS: "Dalam Perjalanan",
    RETURNING: "Kembali ke Depot",
    POST_TRIP_CHECK: "Pemeriksaan Akhir",
    CLOSED: "Selesai",
  };
  return labels[value] || value || "Belum dimulai";
}

function actionLabel(action) {
  if (action === "VOID_ACTIVE_STOP_AND_RESET") return "Reset Aktivitas Stop dan Eksekusi";
  if (action === "RESET_TO_PREPARATION") return "Reset Eksekusi Fleet Order";
  return "Reset tidak tersedia";
}

export function FleetOrderOperationalOverrideDialog({ fleetOrder, onApplied, onClose }) {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [vehicleStillAtDepotConfirmed, setVehicleStillAtDepotConfirmed] = useState(false);
  const [evidenceResetConfirmed, setEvidenceResetConfirmed] = useState(false);
  const reasonRef = useRef(null);
  const vehicleConfirmationRef = useRef(null);
  const evidenceConfirmationRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getFleetOrderOperationalOverrideWorkspace(fleetOrder.id)
      .then((payload) => {
        if (active) setWorkspace(payload);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fleetOrder.id]);

  const action = workspace?.recommendedAction;
  const evidencePresent = workspace?.requiresEvidenceResetConfirmation === true;
  const requirements = useMemo(() => {
    const values = [];
    if (reason.trim().length < 20) values.push("isi alasan minimal 20 karakter");
    if (!vehicleStillAtDepotConfirmed) values.push("konfirmasi kendaraan masih di depot");
    if (evidencePresent && !evidenceResetConfirmed) {
      values.push("konfirmasi aktivitas selesai/bukti berasal dari salah pengoperasian");
    }
    return values;
  }, [
    evidencePresent,
    evidenceResetConfirmed,
    reason,
    vehicleStillAtDepotConfirmed,
  ]);
  const canSubmit = Boolean(
    action &&
    workspace?.canResetExecution &&
    requirements.length === 0,
  );

  function focusFirstRequirement() {
    if (reason.trim().length < 20) {
      reasonRef.current?.focus?.();
      return;
    }
    if (!vehicleStillAtDepotConfirmed) {
      vehicleConfirmationRef.current?.focus?.();
      return;
    }
    if (evidencePresent && !evidenceResetConfirmed) {
      evidenceConfirmationRef.current?.focus?.();
    }
  }

  async function submit() {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError("");
    try {
      const payload = await applyFleetOrderOperationalOverride(fleetOrder.id, {
        overrideType: action,
        reason: reason.trim(),
        vehicleStillAtDepotConfirmed,
        evidenceResetConfirmed,
      });
      onApplied(payload);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      className="dialog-card--fleet-override dialog-card--fleet-execution-reset"
      onClose={() => !busy && onClose()}
      title="Reset Eksekusi Fleet Order"
    >
      <div className="dialog-card__body fleet-override-dialog fleet-execution-reset-dialog">
        <div className="fleet-execution-reset-intro" role="alert">
          <Icon name="warning" size={21} />
          <div>
            <strong>Gunakan hanya untuk salah pengoperasian saat kendaraan masih di depot</strong>
            <span>
              Status perjalanan dan stop dikembalikan ke Persiapan. Event, foto, dan histori lama tidak dihapus.
            </span>
          </div>
        </div>

        {error && <div className="error-banner" role="alert">{error}</div>}

        {workspace?.resetWarnings?.map((warning) => (
          <div className="fleet-execution-reset-evidence" key={warning} role="note">
            <Icon name="warning" size={19} />
            <div>
              <strong>Perlu konfirmasi operasional</strong>
              <span>{warning}</span>
            </div>
          </div>
        ))}

        {loading ? (
          <div className="fleet-amendment-state">Memeriksa kondisi eksekusi Fleet Order…</div>
        ) : !workspace?.canResetExecution || !action ? (
          <div className="fleet-amendment-state is-blocked">
            <Icon name="warning" size={22} />
            <strong>Eksekusi tidak dapat direset</strong>
            <span>{workspace?.resetBlockedReasons?.[0] || "Kondisi Fleet Order tidak memenuhi syarat reset."}</span>
          </div>
        ) : (
          <>
            <section className="fleet-execution-reset-summary" aria-label="Ringkasan eksekusi Fleet Order">
              <div>
                <span>Fleet Order</span>
                <strong>{fleetOrder.foNumber}</strong>
              </div>
              <div>
                <span>Fase saat ini</span>
                <strong>{phaseLabel(workspace.execution?.operationalPhase)}</strong>
              </div>
              <div>
                <span>Stop pernah dijalankan</span>
                <strong>{workspace.execution?.startedStopCount || 0}</strong>
              </div>
              <div>
                <span>Stop berstatus selesai</span>
                <strong>{workspace.execution?.completedStopCount || 0}</strong>
              </div>
              <div>
                <span>Foto/bukti tersimpan</span>
                <strong>{workspace.execution?.stopMediaCount || 0}</strong>
              </div>
            </section>

            <section className="fleet-execution-reset-impact">
              <div className="fleet-execution-reset-impact__heading">
                <Icon name="refresh" size={19} />
                <div>
                  <strong>{actionLabel(action)}</strong>
                  <span>Setelah reset, dialog Tambahkan Pesanan Darurat akan dibuka otomatis.</span>
                </div>
              </div>
              <ul>
                <li>Stop yang sempat dijalankan dikembalikan ke status Pending.</li>
                <li>Perjalanan dikembalikan ke Persiapan di Depot.</li>
                <li>Loading dan urutan rute harus dikonfirmasi ulang.</li>
                <li>Histori lama tetap tersedia sebagai audit.</li>
              </ul>
            </section>

            {evidencePresent && (
              <div className="fleet-execution-reset-evidence" role="note">
                <Icon name="warning" size={19} />
                <div>
                  <strong>Ditemukan status selesai atau bukti stop</strong>
                  <span>
                    Reset tetap diizinkan dengan hak khusus, tetapi bukti tersebut tidak dihapus dan akan tercatat pada audit reset.
                  </span>
                </div>
              </div>
            )}

            <section className="fleet-execution-reset-form" aria-labelledby="fleet-execution-reset-form-title">
              <div className="fleet-execution-reset-form__heading">
                <span>Konfirmasi reset</span>
                <h3 id="fleet-execution-reset-form-title">Alasan dan konfirmasi operasional</h3>
              </div>

              <label className="fleet-execution-reset-reason">
                <span>Alasan reset</span>
                <textarea
                  maxLength={500}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Contoh: Driver salah menekan mulai dan menyelesaikan stop saat kendaraan masih di kantor."
                  ref={reasonRef}
                  rows={4}
                  value={reason}
                />
                <small>{reason.trim().length}/500 · minimal 20 karakter</small>
              </label>

              <div className="fleet-execution-reset-confirmations">
                <label>
                  <input
                    checked={vehicleStillAtDepotConfirmed}
                    onChange={(event) => setVehicleStillAtDepotConfirmed(event.target.checked)}
                    ref={vehicleConfirmationRef}
                    type="checkbox"
                  />
                  <span>
                    <strong>Saya memastikan kendaraan dan driver masih berada di depot.</strong>
                    <small>Reset tidak boleh digunakan jika perjalanan nyata sudah berlangsung.</small>
                  </span>
                </label>

                {evidencePresent && (
                  <label>
                    <input
                      checked={evidenceResetConfirmed}
                      onChange={(event) => setEvidenceResetConfirmed(event.target.checked)}
                      ref={evidenceConfirmationRef}
                      type="checkbox"
                    />
                    <span>
                      <strong>Saya memastikan status selesai dan bukti tersebut berasal dari salah pengoperasian.</strong>
                      <small>Event dan media lama akan dipertahankan sebagai audit.</small>
                    </span>
                  </label>
                )}
              </div>

            </section>
          </>
        )}
      </div>

      {!loading && action && workspace?.canResetExecution && (
        <div className="dialog-card__actions fleet-override-actions fleet-execution-reset-actions">
          <div className="fleet-override-actions__status" aria-live="polite">
            <strong>{canSubmit ? "Reset siap dijalankan" : `${requirements.length} persyaratan belum lengkap`}</strong>
            <span>
              {canSubmit
                ? "Setelah reset, pilih Sales Order tambahan pada dialog berikutnya."
                : `Berikutnya: ${requirements[0] || "lengkapi formulir"}.`}
            </span>
          </div>
          <div className="fleet-override-actions__buttons">
            <button className="secondary-button" disabled={busy} onClick={onClose} type="button">
              Batal
            </button>
            <button
              className="danger-button fleet-override-submit"
              disabled={busy}
              onClick={canSubmit ? submit : focusFirstRequirement}
              type="button"
            >
              {busy ? "Mereset…" : canSubmit ? "Reset Eksekusi" : "Lengkapi Persyaratan"}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
